import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell } from "recharts";
import type { ColumnarTable, PieChartConfig } from "@stockline/shared/types/bi";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/shadcn/chart";
import { toChartNumber } from "@stockline/shared/validation/bi/numeric";
import { cn } from "@/lib/utils";
import { makeCategoryFormatter, formatValue } from "./format";
import type { ChartVariant } from "./types";

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

interface PieChartRendererProps {
  table: ColumnarTable;
  config: PieChartConfig;
  title?: string;
  /** Fixed pixel height for the "default" (chat) variant, whose parent has no
   *  definite height. Ignored by the "card" variant, which fills its card. */
  height?: number;
  /** "card" uses a compact legend + smaller radius so the pie and legend fit
   *  a dashboard cell, and fills the card body (the card owns the height) so
   *  resizing reflows the chart instead of cropping it; "default" keeps the
   *  full chat layout. */
  variant?: ChartVariant;
  /** Enter animation — off for an off-screen export capture so html2canvas
   *  rasterizes the final pie, not a mid-animation frame. */
  animate?: boolean;
}

export function PieChartRenderer({
  table,
  config,
  title,
  height = 300,
  variant = "default",
  animate = true,
}: PieChartRendererProps) {
  const { t } = useTranslation();

  const labelIdx = table.columns.indexOf(config.label_column);
  const valueIdx = table.columns.indexOf(config.value_column);

  // No rows, OR a configured column no longer exists in the result (name drift
  // → `indexOf` is -1). Either way there is nothing meaningful to plot, so show
  // the placeholder rather than a chart of empty slices.
  if (
    !table.data ||
    table.data.length === 0 ||
    labelIdx === -1 ||
    valueIdx === -1
  ) {
    return (
      <div className="text-muted-foreground text-sm p-4">
        {t("charts.noData", "No data to display")}
      </div>
    );
  }

  const card = variant === "card";

  const formatLabel = makeCategoryFormatter(
    table.data.map((row) => row[labelIdx]),
  );
  const chartData = table.data.map((row, i) => ({
    name: formatLabel(row[labelIdx]),
    value: toChartNumber(row[valueIdx]),
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const chartConfig = Object.fromEntries(
    chartData.map((d) => [d.name, { label: d.name }]),
  ) satisfies ChartConfig;

  return (
    <div className="h-full space-y-2">
      {/* The card variant fills its height and never shows its own title (the
          WidgetCard header owns it); rendering one here would both duplicate
          the title and overflow the h-full chart div below. */}
      {title && !card && <h4 className="font-medium text-sm">{title}</h4>}
      <div
        className={card ? "h-full w-full" : "w-full"}
        style={card ? undefined : { height }}
      >
        <div className="flex h-full w-full items-stretch gap-3">
          <ChartContainer
            config={chartConfig}
            className={cn(
              "aspect-auto h-full min-w-0 flex-1",
              // CSS conic sweep replaces recharts' JS animation (unreliable on a
              // 0×0 initial mount). Mount-only, so a poll refresh re-rendering
              // the same card never replays it.
              animate && "animate-pie-sweep",
            )}
          >
            <PieChart>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    nameKey="name"
                    hideLabel
                    valueFormatter={(v) => formatValue(Number(v), config.unit)}
                  />
                }
              />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percent }) =>
                  percent && percent >= 0.05
                    ? `${(percent * 100).toFixed(0)}%`
                    : ""
                }
                outerRadius={card ? "70%" : "80%"}
                // recharts' JS animation is disabled — the CSS conic sweep on
                // the container is the entrance (reliable on mount, unlike
                // recharts' 0×0-mount race). Keeping both would double-animate.
                isAnimationActive={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`${entry.name}-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          {/* Vertical legend beside the pie: one label per row. When the names
              overflow — a small/resized card or many slices — the list scrolls
              within the card instead of spilling past it, and long names
              truncate (full text on hover). Centers vertically when they fit. */}
          <div
            className="h-full min-h-0 max-w-[45%] overflow-y-auto"
            data-testid="pie-legend"
          >
            <ul className="flex min-h-full flex-col justify-center gap-1 py-0.5 text-xs">
              {chartData.map((entry, index) => (
                <li
                  key={`${entry.name}-${index}`}
                  className="flex items-center gap-1.5"
                >
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: entry.fill }}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-muted-foreground"
                    title={entry.name}
                  >
                    {entry.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
